import { useState, useMemo, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import clsx from 'clsx'
import { ForecastHistoryItem } from '../../utils/api'
import { ForecastCard } from './ForecastCard'
import { categoryConfig } from './constants'

interface ForecastSidebarProps {
  forecasts: ForecastHistoryItem[]
  totalForecasts: number
  avgProbability: number | null
  activeForecastId: string | null
  selectedForecast: string | null
  onSelectForecast: (id: string) => void
  onDeleteForecast: (id: string) => void
  isCollapsed: boolean
  onToggleCollapse: () => void
}

// Simple virtualized list component (no external deps)
function VirtualizedList({
  items,
  renderItem,
  itemHeight = 120,
  containerHeight = 400,
}: {
  items: any[]
  renderItem: (item: any, index: number) => React.ReactNode
  itemHeight?: number
  containerHeight?: number
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)

  const handleScroll = () => {
    if (containerRef.current) {
      setScrollTop(containerRef.current.scrollTop)
    }
  }

  const visibleItems = useMemo(() => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - 2)
    const endIndex = Math.min(
      items.length,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + 2
    )

    return items.slice(startIndex, endIndex).map((item, i) => ({
      item,
      index: startIndex + i,
      offset: (startIndex + i) * itemHeight,
    }))
  }, [items, scrollTop, itemHeight, containerHeight])

  const totalHeight = items.length * itemHeight

  // For small lists, just render normally
  if (items.length <= 15) {
    return (
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        <AnimatePresence>
          {items.map((item, i) => renderItem(item, i))}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={{ height: containerHeight, overflowY: 'auto' }}
      className="space-y-2"
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {visibleItems.map(({ item, index, offset }) => (
          <div
            key={item.forecast_id}
            style={{
              position: 'absolute',
              top: offset,
              left: 0,
              right: 0,
              height: itemHeight,
              paddingBottom: 8,
            }}
          >
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    </div>
  )
}

export function ForecastSidebar({
  forecasts,
  totalForecasts,
  avgProbability,
  activeForecastId,
  selectedForecast,
  onSelectForecast,
  onDeleteForecast,
  isCollapsed,
  onToggleCollapse,
}: ForecastSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  // Split by status
  const runningForecasts = forecasts.filter(
    (f) => f.status === 'running' || f.status === 'analyzing' || f.status === 'pending'
  )
  const completedForecasts = forecasts.filter(
    (f) => f.status === 'completed' || f.status === 'failed'
  )

  // Apply filters to completed forecasts
  const filteredCompleted = useMemo(() => {
    let result = completedForecasts

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter((f) => f.question.toLowerCase().includes(query))
    }

    if (categoryFilter) {
      result = result.filter((f) => f.category === categoryFilter)
    }

    return result
  }, [completedForecasts, searchQuery, categoryFilter])

  // Used categories for filter
  const usedCategories = useMemo(() => {
    const cats = new Set<string>()
    completedForecasts.forEach((f) => {
      if (f.category) cats.add(f.category)
    })
    return Array.from(cats).sort()
  }, [completedForecasts])

  const hasActiveFilters = searchQuery.trim() || categoryFilter

  // Mobile collapsed view
  if (isCollapsed) {
    return (
      <button
        onClick={onToggleCollapse}
        className="fixed bottom-4 left-4 z-50 lg:hidden glass-card p-3 rounded-full shadow-lg flex items-center gap-2"
      >
        <span className="text-lg">📋</span>
        <span className="text-xs font-mono text-star-muted">{totalForecasts}</span>
        {runningForecasts.length > 0 && (
          <span className="w-2 h-2 bg-signal-blue rounded-full animate-pulse" />
        )}
      </button>
    )
  }

  return (
    <div className="space-y-4">
      {/* Mobile close button */}
      <div className="lg:hidden flex justify-end mb-2">
        <button
          onClick={onToggleCollapse}
          className="p-2 text-star-dim hover:text-star transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Stats */}
      <div className="glass-card p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-star-dim mb-1">Total Forecasts</div>
            <div className="text-2xl font-mono text-star">{totalForecasts}</div>
          </div>
          <div>
            <div className="text-xs text-star-dim mb-1">Avg Probability</div>
            <div className="text-2xl font-mono text-star-muted">
              {avgProbability !== null ? `${(avgProbability * 100).toFixed(0)}%` : '-'}
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="space-y-2">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search forecasts..."
            className="w-full px-3 py-2 pl-9 bg-void-50 border border-star-dim/20 rounded-lg text-sm text-star placeholder:text-star-dim/50 focus:outline-none focus:border-signal-blue/50 transition-all"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-star-dim"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-star-dim hover:text-star"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filter toggle */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={clsx(
              'text-xs font-mono flex items-center gap-1 transition-colors',
              showFilters || hasActiveFilters ? 'text-signal-blue' : 'text-star-dim hover:text-star'
            )}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            {hasActiveFilters && (
              <span className="w-1.5 h-1.5 bg-signal-blue rounded-full" />
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={() => {
                setSearchQuery('')
                setCategoryFilter(null)
              }}
              className="text-xs text-star-dim hover:text-signal-red transition-colors"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Category filters */}
        <AnimatePresence>
          {showFilters && usedCategories.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap gap-1.5 pt-2">
                <button
                  onClick={() => setCategoryFilter(null)}
                  className={clsx(
                    'px-2 py-1 rounded text-xs font-mono transition-all',
                    !categoryFilter
                      ? 'bg-signal-blue/20 text-signal-blue border border-signal-blue/30'
                      : 'bg-void-50 text-star-dim border border-star-dim/20 hover:text-star'
                  )}
                >
                  All
                </button>
                {usedCategories.map((cat) => {
                  const config = categoryConfig[cat] || categoryConfig.Other
                  return (
                    <button
                      key={cat}
                      onClick={() => setCategoryFilter(cat === categoryFilter ? null : cat)}
                      className={clsx(
                        'px-2 py-1 rounded text-xs font-mono transition-all flex items-center gap-1',
                        cat === categoryFilter
                          ? `${config.bgClass} ${config.textClass} border border-current/30`
                          : 'bg-void-50 text-star-dim border border-star-dim/20 hover:text-star'
                      )}
                    >
                      <span>{config.icon}</span>
                      {cat}
                    </button>
                  )
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Running */}
      {runningForecasts.length > 0 && (
        <div>
          <h3 className="text-sm font-mono text-star-dim mb-2 flex items-center gap-2">
            <span className="w-2 h-2 bg-signal-blue rounded-full animate-pulse" />
            In Progress ({runningForecasts.length})
          </h3>
          <div className="space-y-2">
            <AnimatePresence>
              {runningForecasts.map((forecast) => (
                <ForecastCard
                  key={forecast.forecast_id}
                  forecast={forecast}
                  isActive={forecast.forecast_id === activeForecastId}
                  onClick={() => onSelectForecast(forecast.forecast_id)}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <h3 className="text-sm font-mono text-star-dim mb-2 flex items-center justify-between">
          <span>History</span>
          {hasActiveFilters && (
            <span className="text-xs text-star-dim/50">
              {filteredCompleted.length} of {completedForecasts.length}
            </span>
          )}
        </h3>
        {filteredCompleted.length > 0 ? (
          <VirtualizedList
            items={filteredCompleted}
            renderItem={(forecast) => (
              <ForecastCard
                key={forecast.forecast_id}
                forecast={forecast}
                isActive={forecast.forecast_id === selectedForecast}
                onClick={() => onSelectForecast(forecast.forecast_id)}
                onDelete={() => onDeleteForecast(forecast.forecast_id)}
              />
            )}
          />
        ) : completedForecasts.length > 0 ? (
          <div className="glass-card p-6 text-center">
            <p className="text-star-dim text-sm">No matching forecasts</p>
            <button
              onClick={() => {
                setSearchQuery('')
                setCategoryFilter(null)
              }}
              className="text-xs text-signal-blue hover:text-signal-blue/80 mt-2"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="glass-card p-8 text-center">
            <p className="text-star-dim text-sm">No forecasts yet</p>
            <p className="text-star-dim/50 text-xs mt-1">Ask a question to get started</p>
          </div>
        )}
      </div>
    </div>
  )
}
